/* VideoComponent */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, h, Prop, State, Watch } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import VideoTemplate from './cwc-video.template';
import { AbstractComponent, Slot } from '@cewe-designsystem/abstract';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';
import { VideoType } from './videoType';

/**
 * @slot headline - the headline content slot
 * @slot description - the description content slot
 */
@Component({
  tag: 'cwc-video',
  styleUrl: './cwc-video.scss',
  shadow: true,
})
export class VideoComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;

  /**
   * The slotted element for the headline.
   *
   * @var {HTMLElement}
   */
  @Slot('headline') headlineElement;

  /**
   * The slotted element for the description.
   *
   * @var {HTMLElement}
   */
  @Slot('description') descriptionElement;

  /**
   * The URL of the poster to be shown while the video is loading.
   *
   * @docs
   * @property {string} posterUrl
   */
  @Prop({ reflect: true })
  posterUrl: string;

  /**
   * The video URL used for desktop devices.
   *
   * @docs
   * @property {string} videoUrlDesktop
   */
  @Prop({ reflect: true })
  videoUrlDesktop: string;

  /**
   * The video URL used for mobile devices.
   *
   * @docs
   * @property {string} videoUrlMobile
   */
  @Prop({ reflect: true })
  videoUrlMobile: string;

  /**
   * The video's file type.
   *
   * @docs
   * @property {VideoType} videoType
   */
  @Prop({ reflect: true })
  videoType: VideoType = VideoType.AUTO;

  /**
   * Autoplay the video.
   *
   * @docs
   * @property {boolean} autoplay
   */
  @Prop({ reflect: true })
  autoplay: boolean = false;

  /**
   * Run the video in an endless loop.
   *
   * @docs
   * @property {boolean} loop
   */
  @Prop({ reflect: true })
  loop: boolean = false;

  /**
   * Defaults to true to allow autoplay on iOS > 14
   *
   * @docs
   * @property {boolean} playsInline
   */
  @Prop({ reflect: true })
  playsInline: boolean = true;

  /**
   * Play the video in the muted mode.
   *
   * @docs
   * @property {boolean} muted
   */
  @Prop({ reflect: true })
  muted: boolean = false;

  /**
   * Breakpoint used for switching from mobile to desktop mode.
   *
   * @docs
   * @property {string} desktopBreakpoint
   */
  @Prop() desktopBreakpoint: string = 'sm';

  _breakpointHandler: UtilBreakpoints = new UtilBreakpoints();

  /**
   * Indicator if it's a mobile or desktop device.
   */
  @State() isMobile = this._breakpointHandler.breakpointDown(this.desktopBreakpoint);

  /**
   * The URL of the video to be shown.
   * It contains the mobile or desktop video URL (depending on the device size).
   */
  @State() videoUrl: string;

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, every time, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.updateVideoUrl();
  }

  /**
   * Event handler for updating the currently shown video URL.
   */
  @Watch('videoUrlDesktop')
  @Watch('videoUrlMobile')
  updateVideoUrl() {
    this.videoUrl = this.isMobile && this.videoUrlMobile ? this.videoUrlMobile : this.videoUrlDesktop;
  }

  /**
   * Get the video's file type.
   *
   * @return {string}
   */
  getVideoType(): string {
    if (this.videoType !== VideoType.AUTO) {
      return this.videoType;
    }

    // Try to determine the video type based on the video's filename
    return this.videoUrl.split('.').pop();
  }

  /**
   * @inheritDoc
   * @TODO: Currently this empty method is required to make the Slot decorator work
   */
  componentWillRender() {}

  /**
   * @inheritDoc
   */
  render() {
    return <VideoTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(VideoComponent.prototype, AbstractComponent.prototype);

export interface VideoComponent extends AbstractComponent {}
